import React, { useMemo, useState } from 'react';
import { Button, Dialog, Spinner, PaginatedTable, Group } from '@economic/taco';
import { useNavigate } from 'react-router-dom';
import { t } from '@lingui/macro';
import number from '../../../../shared/util/utilities/number';
import FiltersAndActions from '../../../../views/components/FiltersAndActions';
import ConfirmationDialog from '../../../../shared/components/modals/ConfirmationDialog';
import { appendSimpleSearchFilter } from '../../../../../utilities/filters';
import View from '../../../../shared/components/View/View';
import { useCollectionQuery } from '../../../../shared/hooks/useCollectionQuery';
import useLocalStorage from '../../../../shared/hooks/useLocalStorage';
import internalApiClient from '../../../../shared/util/api/internalApiClient';
import { NewProductCollectionDialog } from './dialogs/NewProductCollectionDialog';
import { EditProductCollectionDialog } from './dialogs/EditProductCollectionDialog';
import emptyState from './product-collections-documents.svg';
import { ProductCollectionListViewModel } from '../../../../../types/internal-api';
import { CopyProductCollectionDialog } from './dialogs/CopyProductCollectionDialog';
import useKeyShortcut from '../../../../shared/hooks/useKeyShortcut';

interface Props {
    isComboboxView?: boolean;
    onSelected?: (selected: ProductCollectionListViewModel) => void;
}

export default function ProductCollections({ isComboboxView, onSelected }: Props) {
    const [createNewCollection, setCreateNewCollection] = useState(false);
    const [collectionToCopy, setCollectionToCopy] = useState<number | null>(null);
    const [collectionToDelete, setCollectionToDelete] = useState<number | null>(null);
    const [collectionToEdit, setCollectionToEdit] = useState<number | null>(null);
    const simpleSearchFields = ['id', 'name'];
    const [searchQuery, setSearchQuery] = useLocalStorage(`product-collections-collection-search-bar`, '');
    useKeyShortcut('n', () => setCreateNewCollection(true));
    const { data, loading, refetch, sorting, pagination } = useCollectionQuery<ProductCollectionListViewModel>(
        `/products/product-collections?filter=${appendSimpleSearchFilter('', searchQuery, simpleSearchFields)}`,
        false,
        'product-collections-collection'
    );
    const navigate = useNavigate();

    const emptyStateRenderer = () => {
        if (!loading && data.length === 0) {
            return (
                <div className="flex flex-col w-72 mx-auto mt-12">
                    <img src={emptyState} className="w-5/6 mx-auto" />
                    <h3 className="text-center">{t`Create a product collection`}</h3>
                    <p>{t`Product collections make it faster to create quotes, orders, invoices if you sell package solutions or if you have customers who always sell the same products. Make a collection once and use it again and again.`}</p>

                    <Button appearance="primary" onClick={() => setCreateNewCollection(true)}>
                        {t`New Collection`}
                    </Button>
                </div>
            );
        }
        return <></>;
    };

    return useMemo(
        () =>
            loading ? (
                <Spinner />
            ) : (
                <View>
                    <View.Title>{t`Product Collections`}</View.Title>

                    <View.Header>
                        <Button appearance="primary" onClick={() => setCreateNewCollection(true)}>
                            {t`New Collection`}
                        </Button>
                        <View.Header.Filters>
                            <Group>
                                <FiltersAndActions
                                    filter={searchQuery}
                                    onFilter={setSearchQuery}
                                    hideAdvancedSearch
                                    hidePrint={isComboboxView}
                                    hideExport={isComboboxView}
                                    excelExporter={{
                                        basePath: 'products/product-collections',
                                        dataEndpoint: 'products/product-collections',
                                        exportEndpoint: 'products/product-collections/excel-sheet',
                                        filename: `${t`ProductCollections`}.xlsx`,
                                        headlines: [t`Product Collections`],
                                        columns: [
                                            { accessor: 'id', columnHeader: t`No.` },
                                            { accessor: 'name', columnHeader: t`Name` },
                                            { accessor: 'totalAmount', columnHeader: t`Total Amount` },
                                        ],
                                    }}
                                />
                            </Group>
                        </View.Header.Filters>
                    </View.Header>

                    <View.Content>
                        <PaginatedTable<ProductCollectionListViewModel>
                            data={data}
                            {...pagination}
                            {...sorting}
                            emptyStateRenderer={emptyStateRenderer}
                            onRowEdit={!isComboboxView ? row => setCollectionToEdit(row.values.id) : undefined}
                            onRowDelete={!isComboboxView ? row => setCollectionToDelete(row.values.id) : undefined}
                            onRowCopy={!isComboboxView ? row => setCollectionToCopy(row.values.id) : undefined}
                            onRowClick={onSelected === undefined ? undefined : row => onSelected(row.values)}
                            id="product-collections"
                            actions={[
                                {
                                    icon: 'templates',
                                    text: t`Product lines`,
                                    onClick: row => {
                                        navigate(`/sales/products/product-collections/collection/${row.values.salesDocumentId}`);
                                    },
                                    tooltip: t`Product lines`,
                                    showOnRow: true,
                                    disabled: isComboboxView,
                                    visible: !isComboboxView,
                                },
                            ]}>
                            <PaginatedTable.Column accessor="id" headRenderer={t`No.`} align="left" />
                            <PaginatedTable.Column accessor="name" headRenderer={t`Collection Name`} align="left" />
                            <PaginatedTable.Column
                                accessor="totalAmount"
                                headRenderer={t`Total Price`}
                                cellRenderer={({ value }) => number.format(value)}
                                align="left"
                            />
                        </PaginatedTable>

                        {!isComboboxView && collectionToCopy && (
                            <Dialog size="xs" defaultOpen onClose={() => setCollectionToCopy(null)}>
                                <Dialog.Content aria-label="Copy">
                                    <CopyProductCollectionDialog
                                        productCollectionId={collectionToCopy}
                                        onHide={() => setCollectionToCopy(null)}
                                        refetch={refetch}
                                    />
                                </Dialog.Content>
                            </Dialog>
                        )}

                        {!isComboboxView && collectionToEdit && (
                            <Dialog size="xs" defaultOpen onClose={() => setCollectionToEdit(null)}>
                                <Dialog.Content aria-label="Edit">
                                    <EditProductCollectionDialog
                                        idKeyProp="id"
                                        productCollectionId={collectionToEdit}
                                        collectionData={data}
                                        onHide={() => setCollectionToEdit(null)}
                                        refetch={refetch}
                                        currentItemId={collectionToEdit}
                                        onArrowKeySaveAndChange={nextLineNumber => setCollectionToEdit(nextLineNumber as number)}
                                    />
                                </Dialog.Content>
                            </Dialog>
                        )}

                        {createNewCollection && (
                            <Dialog
                                size="xs"
                                defaultOpen
                                onClose={() => {
                                    refetch();
                                    setCreateNewCollection(false);
                                }}>
                                <Dialog.Content aria-label="">
                                    <NewProductCollectionDialog
                                        onHide={() => setCreateNewCollection(false)}
                                        refetch={refetch}
                                        onClose={() => {
                                            refetch();
                                            setCreateNewCollection(false);
                                        }}
                                    />
                                </Dialog.Content>
                            </Dialog>
                        )}

                        {!isComboboxView && collectionToDelete && (
                            <Dialog size="dialog" defaultOpen onClose={() => setCollectionToDelete(null)}>
                                <Dialog.Content aria-label="Delete">
                                    <ConfirmationDialog
                                        label={t`Confirm`}
                                        focusConfirmButton
                                        message={t`Are you sure you want to delete this product collection?`}
                                        onConfirm={async () => {
                                            await internalApiClient.delete(`products/product-collections/${collectionToDelete}`);
                                            setCollectionToDelete(null);
                                            await refetch();
                                        }}
                                        onCancel={() => setCollectionToDelete(null)}
                                        confirmText={t`Ok`}
                                    />
                                </Dialog.Content>
                            </Dialog>
                        )}
                    </View.Content>
                </View>
            ),
        [data, searchQuery, loading, createNewCollection, collectionToCopy, collectionToDelete, collectionToEdit]
    );
}
